import React from 'react';
import { ExternalLink, Shield, FileCheck, AlertCircle } from 'lucide-react';

const Share = () => {
  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Header */}
      <section className="py-12 bg-navy">
        <div className="container mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
            Share Your Experience
          </h1>
          <p className="text-xl text-gray-300 text-center max-w-3xl mx-auto leading-relaxed">
            Your voice and lived experience are essential to building evidence for systemic change. Share your story 
            anonymously to help identify response gaps, strengthen accountability, and improve outcomes for vulnerable 
            youth and families actively navigating complex systems across Ontario.
          </p>
        </div>
      </section>

      {/* Privacy Notice */}
      <section className="py-8 bg-charcoal border-b border-gold-dim">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="privacy-notice flex items-start gap-4 p-6 bg-navy/50 border border-gold-dim rounded-lg mb-6">
              <Shield size={24} className="text-gold flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-white mb-2">
                  Complete Anonymity & Confidentiality
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  <strong className="text-white">Please do not share names of minors or identifying details of youth.</strong> 
                  Anonymous submissions are encouraged. All responses are confidential and used exclusively for research, 
                  systems analysis, and policy advocacy to improve institutional responses for vulnerable youth.
                </p>
              </div>
            </div>

            <div className="privacy-notice flex items-start gap-4 p-6 bg-navy/50 border border-gold-dim rounded-lg">
              <AlertCircle size={24} className="text-gold flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-white mb-2">
                  This Is Not an Emergency Tool
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  FOMB is an advocacy and support initiative and does not replace emergency services. 
                  If a youth is currently missing or in immediate danger, <strong className="text-gold">call 911</strong> immediately.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Buttons */}
      <section className="py-8 bg-charcoal">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <a
              href="https://form.jotform.com/260245121503038"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            >
              Open Form in New Tab
              <ExternalLink size={20} />
            </a>
            <p className="text-gray-500 text-sm mt-4">
              Or fill out the form below
            </p>
          </div>
        </div>
      </section>

      {/* Embedded Form */}
      <section className="py-12 bg-charcoal">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="form-embed-container bg-white rounded-lg overflow-hidden shadow-2xl">
              <iframe
                src="https://form.jotform.com/260245121503038"
                title="Share Your Experience Form"
                className="w-full"
                style={{ minHeight: '800px', border: 'none' }}
                allow="geolocation; microphone; camera"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-16 bg-navy">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-6">
                <FileCheck size={32} className="text-gold" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                How Your Story Supports Change
              </h2>
              <p className="text-gray-300 leading-relaxed">
                Every lived experience shared helps us identify patterns, document systemic gaps, and build 
                evidence for advocacy and policy reform.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-charcoal p-6 rounded-lg border border-gold-dim">
                <h3 className="text-xl font-bold text-white mb-3">1. Pattern Recognition & Systems Analysis</h3>
                <p className="text-gray-400 leading-relaxed">
                  We analyze submissions to identify consistent gaps in institutional responses—delayed action, 
                  inadequate follow-up, poor coordination across agencies, and dismissal of vulnerability indicators.
                </p>
              </div>

              <div className="bg-charcoal p-6 rounded-lg border border-gold-dim">
                <h3 className="text-xl font-bold text-white mb-3">2. Confidential Data Aggregation</h3>
                <p className="text-gray-400 leading-relaxed">
                  All responses remain confidential. We aggregate anonymized data to understand systemic issues 
                  without exposing individual stories or identities.
                </p>
              </div>

              <div className="bg-charcoal p-6 rounded-lg border border-gold-dim">
                <h3 className="text-xl font-bold text-white mb-3">3. Policy Advocacy & Institutional Engagement</h3>
                <p className="text-gray-400 leading-relaxed">
                  Community-based evidence strengthens our advocacy for policy changes, improved training, 
                  better coordination protocols, and enhanced accountability across justice, child welfare, 
                  education, and community safety sectors.
                </p>
              </div>

              <div className="bg-charcoal p-6 rounded-lg border border-gold-dim">
                <h3 className="text-xl font-bold text-white mb-3">4. Consent Always Required for Public Stories</h3>
                <p className="text-gray-400 leading-relaxed">
                  <strong className="text-white">Separate explicit consent is always required</strong> before 
                  any story or experience is shared publicly. We never publish personal accounts without direct permission.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Share;
